import { money, pct } from './format.js';
import RothConversionPanel from './RothConversionPanel.jsx';

// The consequence of the tax-pool picture above, made concrete in two ways.
//
// First: a tax-deferred balance is not just money, it is a future tax bill on a
// timer. Most clients have never seen the size of the withdrawal the IRS will
// eventually require, and seeing it is what turns "we should look at Roth
// conversions someday" into a dated decision.
//
// Second: where each kind of asset is held. This one is unglamorous and almost
// always worth real money, and it is the clearest possible demonstration that
// somebody read the actual statements rather than the account totals.

export default function TaxHorizonSection({ rmd, roth, location, names }) {
  if (!rmd && !location?.findings?.length) return null;

  return (
    <section className="asmt-card" id="tax-horizon">
      <div className="asmt-card-head">
        <h2>The part of the tax bill nobody has shown you</h2>
      </div>

      {rmd && (
        <>
          <p className="asmt-lede">
            {rmd.alreadyStarted
              ? `At ${rmd.ageNow}, required minimum distributions have already begun.`
              : `${rmd.ownerName} turns ${rmd.startAge} in ${rmd.firstYear}. `
                + 'From that year the government sets a minimum you have to withdraw from the '
                + 'tax-deferred accounts, whether or not you need the money.'}
          </p>

          {/* Withdrawals already under way: the pool and the question, and no
              figure. Working the amount out needs each account's balance last
              31 December, who owns it, and a date of birth, none of which the
              questionnaire asks for. It used to answer anyway, by starting the
              schedule at the age the client passed years ago and walking
              today's balance from there, then labelling the result "required
              this year". */}
          {rmd.alreadyStarted ? (
            <>
              <div className="asmt-tiles" style={{ marginTop: 0 }}>
                <div className="asmt-tile">
                  <div className="asmt-tile-label">In tax-deferred accounts</div>
                  <div className="asmt-tile-value">{money(rmd.balance)}</div>
                  <div className="asmt-tile-note">every dollar still owing income tax</div>
                </div>
              </div>
              <div className="asmt-callout">
                <strong>What we need from you.</strong>{' '}
                Which accounts are you already drawing from, and how much came out last
                year? The amount is set per account, from its balance on the last day of
                the previous year and the owner's age, so it is worth getting from the
                statements rather than estimated here.
              </div>
            </>
          ) : (
          <>
          <div className="asmt-tiles" style={{ marginTop: 0 }}>
            <div className="asmt-tile">
              <div className="asmt-tile-label">First required withdrawal, {rmd.firstYear}</div>
              <div className="asmt-tile-value">{money(rmd.firstAmount)}</div>
              <div className="asmt-tile-note">taxed as ordinary income</div>
            </div>
            <div className="asmt-tile">
              <div className="asmt-tile-label">Forced out by age {rmd.horizonAge}</div>
              <div className="asmt-tile-value is-gap">{money(rmd.totalToHorizon)}</div>
              <div className="asmt-tile-note">across {rmd.schedule.length} years</div>
            </div>
            <div className="asmt-tile">
              <div className="asmt-tile-label">Still in the account at {rmd.horizonAge}</div>
              <div className="asmt-tile-value">{money(rmd.remainingAtHorizon)}</div>
              <div className="asmt-tile-note">still owing income tax</div>
            </div>
          </div>

          <div className="asmt-callout">
            <strong>An illustration, on the household's whole tax-deferred balance.</strong>{' '}
            Every figure here assumes the account earns nothing between now and then, so
            if it grows at all the balance is larger and so is every required withdrawal.
            {rmd.ownershipAssumed
              ? ' It also times the whole balance off whichever of you is older, so the'
                + ' real schedule depends on who owns which account.'
              : ''}
            {' '}What you can still change is the timing: dollars moved out deliberately,
            in years you choose, are dollars the schedule never gets to decide for you.
          </div>
          </>
          )}
        </>
      )}

      {rmd && (
        <RothConversionPanel roth={roth} />
      )}

      {location?.findings?.length > 0 && (
        <div className="asmt-subsection">
          <h3 className="asmt-subhead">
            {location.findings.length === 1
              ? 'One thing is in the wrong account'
              : `${location.findings.length} things are in the wrong account`}
          </h3>
          <p className="asmt-note">
            Which account an investment sits in changes what you keep, even when the
            investment itself does not change at all. Reading your statements, {money(location.misplacedValue)} is
            positioned in a way that costs you more tax than it needs to.
          </p>
          <div className="asmt-location-list">
            {location.findings.map(f => (
              <div className="asmt-location" key={f.key}>
                <div className="asmt-location-head">
                  <span className="asmt-location-title">{f.headline}</span>
                  <span className="asmt-location-value">{money(f.value)}</span>
                </div>
                <p className="asmt-location-why">{f.why}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
